import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, FileText } from 'lucide-react';
import { Btn, Modal, FormField, Badge, inputCls } from '../ui';
import { useToast } from '../../contexts/NotificationContext';
import {
  getEmployeeDocumentTypes,
  createEmployeeDocumentType,
  updateEmployeeDocumentType,
  deleteEmployeeDocumentType,
} from '../../api';

const EMPTY = {
  name: '',
  slug: '',
  position: 0,
  isActive: true,
  validityMonths: '',
  mandatory: false,
  category: '',
};

export default function HREmployeeDocumentTypeManagement() {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getEmployeeDocumentTypes();
      setTypes(data);
    } catch {
      toast.error('Erreur chargement types de documents');
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, position: types.length + 1 });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      slug: t.slug,
      position: t.position,
      isActive: t.isActive,
      validityMonths: t.validityMonths != null ? String(t.validityMonths) : '',
      mandatory: t.mandatory,
      category: t.category || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        position: parseInt(form.position) || 0,
        isActive: form.isActive,
        validityMonths: form.validityMonths !== '' ? parseInt(form.validityMonths) : null,
        mandatory: form.mandatory,
        category: form.category.trim() || null,
      };
      // slug : à la création, on laisse le backend l'auto-générer si non fourni
      if (form.slug?.trim()) payload.slug = form.slug.trim();
      if (editing) {
        await updateEmployeeDocumentType(editing.id, payload);
        toast.success('Type mis à jour');
      } else {
        await createEmployeeDocumentType(payload);
        toast.success('Type ajouté');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur enregistrement');
    }
    setSaving(false);
  };

  const handleDelete = async (t) => {
    try {
      await deleteEmployeeDocumentType(t.id);
      toast.success('Type supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    }
    setDeleteConfirm(null);
  };

  const movePosition = async (t, direction) => {
    const newPos = direction === 'up' ? t.position - 1 : t.position + 1;
    try {
      await updateEmployeeDocumentType(t.id, { position: newPos });
      load();
    } catch {
      toast.error('Erreur réordonnancement');
    }
  };

  // Groupement par catégorie pour l'affichage
  const byCategory = types.reduce((acc, t) => {
    const cat = t.category || 'Sans catégorie';
    (acc[cat] = acc[cat] || []).push(t);
    return acc;
  }, {});
  const categories = Object.keys(byCategory).sort((a, b) => {
    if (a === 'Sans catégorie') return 1;
    if (b === 'Sans catégorie') return -1;
    return a.localeCompare(b);
  });

  // Liste des catégories distinctes existantes (pour le datalist du formulaire)
  // Inclut les bootstrap (Embauche/Identité/Paie/Santé/Formation) + les catégories
  // déjà créées en BDD (ex: "Compétences", "test"...).
  const existingCategories = Array.from(
    new Set([
      'Embauche', 'Identité', 'Paie', 'Santé', 'Formation',
      ...types.map((t) => t.category).filter(Boolean),
    ])
  ).sort();

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-rose-100 rounded-xl">
            <FileText size={20} className="text-rose-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Types de documents employés</h2>
            <p className="text-xs text-gray-500">
              Gérer les types de documents pouvant être attachés à une fiche employé
            </p>
          </div>
        </div>
        <Btn variant="primary" size="sm" onClick={openCreate}>
          <Plus size={16} /> Nouveau type
        </Btn>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Chargement...</div>
      ) : types.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">Aucun type configuré</div>
      ) : (
        <div className="space-y-4">
          {categories.map((cat) => (
            <div key={cat} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {cat}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <th className="px-4 py-2 text-left font-medium">Nom</th>
                    <th className="px-4 py-2 text-left font-medium">Slug</th>
                    <th className="px-4 py-2 text-left font-medium">Validité</th>
                    <th className="px-4 py-2 text-left font-medium">Obligatoire</th>
                    <th className="px-4 py-2 text-left font-medium">Position</th>
                    <th className="px-4 py-2 text-left font-medium">Statut</th>
                    <th className="px-4 py-2 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {byCategory[cat].map((t) => (
                    <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-800">{t.name}</td>
                      <td className="px-4 py-2 text-xs font-mono text-gray-500">{t.slug}</td>
                      <td className="px-4 py-2 text-gray-600">
                        {t.validityMonths != null ? `${t.validityMonths} mois` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        {t.mandatory ? (
                          <Badge color="bg-amber-100 text-amber-700">Obligatoire</Badge>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{t.position}</td>
                      <td className="px-4 py-2">
                        {t.isActive ? (
                          <Badge color="bg-green-100 text-green-700">Actif</Badge>
                        ) : (
                          <Badge color="bg-gray-100 text-gray-500">Inactif</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => openEdit(t)} className="p-1.5 text-gray-400 hover:text-primary transition" title="Modifier">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setDeleteConfirm(t)} className="p-1.5 text-gray-400 hover:text-red-500 transition" title="Supprimer">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Modal CRUD */}
      {showModal && (
      <Modal onClose={() => setShowModal(false)} title={editing ? 'Modifier le type' : 'Nouveau type'} size="md">
        <div className="space-y-4">
          <FormField label="Nom" required>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
              placeholder="ex: Certificat médical"
            />
          </FormField>

          {editing && (
            <FormField label="Slug (technique, modification déconseillée)">
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className={inputCls + ' font-mono text-xs'}
                placeholder="ex: certif_medical"
              />
              <p className="text-xs text-amber-600 mt-1">
                ⚠ Modifier le slug brise le lien avec les documents existants
              </p>
            </FormField>
          )}

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Catégorie">
              <input
                type="text"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className={inputCls}
                placeholder="ex: Identité, Embauche..."
                list="hr-doc-categories"
              />
              <datalist id="hr-doc-categories">
                {existingCategories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </FormField>
            <FormField label="Position">
              <input
                type="number"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                className={inputCls}
                min="0"
              />
            </FormField>
          </div>

          <FormField label="Validité (en mois)">
            <input
              type="number"
              value={form.validityMonths}
              onChange={(e) => setForm({ ...form, validityMonths: e.target.value })}
              className={inputCls}
              placeholder="laisser vide si pas de péremption"
              min="1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Utilisé pour signaler les documents périmés (ex: 6 mois pour un justificatif de domicile)
            </p>
          </FormField>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.mandatory}
                onChange={(e) => setForm({ ...form, mandatory: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Obligatoire à l'embauche</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Actif</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <Btn variant="secondary" onClick={() => setShowModal(false)}>Annuler</Btn>
          <Btn variant="primary" onClick={handleSave} loading={saving}>
            {editing ? 'Enregistrer' : 'Créer'}
          </Btn>
        </div>
      </Modal>
      )}

      {/* Confirm suppression */}
      {deleteConfirm && (
      <Modal onClose={() => setDeleteConfirm(null)} title="Supprimer ce type ?" size="sm">
        <p className="text-sm text-gray-700">
          Vous allez supprimer le type <strong>{deleteConfirm?.name}</strong>. Cette action est irréversible.
        </p>
        <p className="text-xs text-amber-600 mt-2">
          Si des documents utilisent ce type, la suppression sera refusée. Vous pouvez plutôt le désactiver.
        </p>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <Btn variant="secondary" onClick={() => setDeleteConfirm(null)}>Annuler</Btn>
          <Btn variant="danger" onClick={() => handleDelete(deleteConfirm)}>Supprimer</Btn>
        </div>
      </Modal>
      )}
    </div>
  );
}
